/**
 * ProfilerService
 *
 * Singleton service for collecting canvas performance profiling data.
 * Replaces the console-based profiler with a data-driven approach
 * that can be visualized in the UI.
 */

import {ProfileEntry, ProfileSession} from '../@types/profiler';

type SessionCallback = (session: ProfileSession) => void;

class ProfilerService {
    private static instance: ProfilerService;

    private isEnabled: boolean = false;
    private entries: { label: string; startTime: number; endTime?: number }[] = [];
    private contextLabel: string = '';
    private metadata: ProfileSession['metadata'] = {};
    private sessionStartTime: number = 0;

    // Subscribers for new session notifications
    private subscribers: Set<SessionCallback> = new Set();

    private constructor() {
        // Load enabled state from localStorage
        const saved = localStorage.getItem('profilerEnabled');
        this.isEnabled = saved === 'true';
    }

    static getInstance(): ProfilerService {
        if (!ProfilerService.instance) {
            ProfilerService.instance = new ProfilerService();
        }
        return ProfilerService.instance;
    }

    /**
     * Enable profiling
     */
    enable(): void {
        this.isEnabled = true;
        localStorage.setItem('profilerEnabled', 'true');
    }

    /**
     * Disable profiling
     */
    disable(): void {
        this.isEnabled = false;
        localStorage.setItem('profilerEnabled', 'false');
    }

    /**
     * Toggle profiling on/off
     */
    toggle(): void {
        if (this.isEnabled) {
            this.disable();
        } else {
            this.enable();
        }
    }

    /**
     * Check if profiling is enabled
     */
    getIsEnabled(): boolean {
        return this.isEnabled;
    }

    /**
     * Subscribe to new session notifications
     */
    subscribe(callback: SessionCallback): () => void {
        this.subscribers.add(callback);
        return (): void => {
            this.subscribers.delete(callback);
        };
    }

    /**
     * Notify all subscribers of a new session
     */
    private notifySubscribers(session: ProfileSession): void {
        this.subscribers.forEach(callback => callback(session));
    }

    /**
     * Start a new profiling session
     */
    start(contextLabel: string, metadata?: ProfileSession['metadata']): void {
        if (!this.isEnabled) return;

        this.contextLabel = contextLabel;
        this.metadata = metadata || {};
        this.entries = [];
        this.sessionStartTime = performance.now();
    }

    /**
     * Mark a checkpoint in the current session
     */
    mark(label: string): void {
        if (!this.isEnabled) return;

        const now = performance.now();

        // Close previous entry if exists
        if (this.entries.length > 0) {
            const prev = this.entries[this.entries.length - 1];
            if (!prev.endTime) {
                prev.endTime = now;
            }
        }

        this.entries.push({ label, startTime: now });
    }

    /**
     * End the current profiling session and return the results
     */
    end(): ProfileSession | null {
        if (!this.isEnabled) return null;
        if (this.sessionStartTime === 0) return null; // No session was started

        const now = performance.now();

        // Close last entry
        if (this.entries.length > 0) {
            const prev = this.entries[this.entries.length - 1];
            if (!prev.endTime) {
                prev.endTime = now;
            }
        }

        // If no marks were made, create a single entry for the entire session
        if (this.entries.length === 0) {
            this.entries.push({
                label: 'total',
                startTime: this.sessionStartTime,
                endTime: now
            });
        }

        const totalTime = now - this.sessionStartTime;

        // Convert to ProfileEntry format with calculated durations and percentages
        const processedEntries: ProfileEntry[] = this.entries.map(entry => {
            const duration = (entry.endTime || now) - entry.startTime;
            const percentage = totalTime > 0 ? (duration / totalTime) * 100 : 0;
            return {
                label: entry.label,
                startTime: entry.startTime - this.sessionStartTime, // Relative to session start
                endTime: (entry.endTime || now) - this.sessionStartTime,
                duration,
                percentage
            };
        });

        const session: ProfileSession = {
            id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            contextLabel: this.contextLabel,
            entries: processedEntries,
            totalTime,
            timestamp: new Date(),
            metadata: this.metadata
        };

        // Reset state
        this.entries = [];
        this.contextLabel = '';
        this.metadata = {};
        this.sessionStartTime = 0;

        // Notify subscribers
        this.notifySubscribers(session);

        return session;
    }
}

// Export singleton instance
export const profilerService = ProfilerService.getInstance();

export default ProfilerService;
